// InstallableRailItem — one chip/card/row in an InstallableRail.
//
// State shape (set by parent via childrenAs:'state'):
//   display    { icon, label, tagline, color? }  — from IntegrationKind.display
//   installed  boolean                            — true when an instance exists
//   domain     string                             — 'integration'|'library'|'mcp'|…
//   kind       string                             — kind key, e.g. 'github'
//   _layout    'rail'|'grid'|'list'              — forwarded from parent

// ── Installed state badge ─────────────────────────────────────────────

export const InstallableStateBadge = {
  tag: 'span',
  padding: 'X2 Y2',
  round: 'Y',
  fontSize: 'Y1',
  fontWeight: '700',
  letterSpacing: '0.04em',
  background: (el, s) => (s.installed ? 'green.1' : 'overlayStrong'),
  color: (el, s) => (s.installed ? 'green' : 'caption'),
  text: (el, s) => (s.installed ? 'Installed' : 'Available')
}

// ── Rail chip (horizontal scroll row) ────────────────────────────────

export const InstallableRailChip = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  gap: 'Y',
  align: 'center left',
  padding: 'Y Z',
  round: 'Z2',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  background: (el, s) => (s.installed ? 'overlayStrong' : 'document'),
  cursor: 'pointer',
  flexShrink: '0',
  whiteSpace: 'nowrap',
  transition: 'B defaultBezier',
  transitionProperty: 'background, border-color',
  ':hover': {
    borderColor: 'blue.5',
    background: 'blue.1'
  },
  '@media(prefers-reduced-motion:reduce)': {
    transition: 'none'
  },

  Icon: {
    fontSize: 'B',
    name: (el, s) => s.display?.icon || s.kind || 'grid',
    color: (el, s) => s.display?.color || 'title'
  },

  Label: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '600',
    color: 'title',
    text: (el, s) => s.display?.label || s.kind || ''
  },

  InstallableStateBadge: {
    hide: (el, s) => !s.installed
  },

  onClick: (e, el, s) => {
    el.call('openInstallableModal', {
      domain: s.domain,
      kind: s.kind,
      installed: s.installed
    })
  }
}

// ── Grid card (responsive 3–4 col grid) ──────────────────────────────

export const InstallableGridCard = {
  tag: 'button',
  type: 'button',
  flow: 'y',
  gap: 'Y',
  padding: 'Z Z2',
  round: 'A',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  background: 'document',
  cursor: 'pointer',
  align: 'start left',
  transition: 'B defaultBezier',
  transitionProperty: 'background, border-color',
  ':hover': {
    borderColor: 'blue.5',
    background: 'blue.1'
  },
  '@media(prefers-reduced-motion:reduce)': {
    transition: 'none'
  },

  CardHeader: {
    flow: 'x',
    gap: 'Y',
    align: 'center space-between',
    width: '100%',

    Icon: {
      fontSize: 'C',
      name: (el, s) => s.display?.icon || s.kind || 'grid',
      color: (el, s) => s.display?.color || 'title'
    },

    InstallableStateBadge: {
      hide: (el, s) => !s.installed
    }
  },

  CardBody: {
    flow: 'y',
    gap: 'X2',
    align: 'start left',

    CardLabel: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => s.display?.label || s.kind || ''
    },

    CardTagline: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      fontWeight: '400',
      color: 'paragraph',
      lineHeight: '1.4',
      overflow: 'hidden',
      text: (el, s) => s.display?.tagline || s.display?.description || ''
    }
  },

  onClick: (e, el, s) => {
    el.call('openInstallableModal', {
      domain: s.domain,
      kind: s.kind,
      installed: s.installed
    })
  }
}

// ── List row (vertical management list) ──────────────────────────────

export const InstallableListRow = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  gap: 'Z',
  align: 'center left',
  padding: 'Y Z2',
  borderWidth: '0 0 1px 0',
  borderStyle: 'solid',
  borderColor: 'line',
  background: 'transparent',
  cursor: 'pointer',
  width: '100%',
  textAlign: 'left',
  transition: 'B defaultBezier',
  transitionProperty: 'background',
  ':hover': {
    background: 'overlayStrong'
  },
  ':last-child': {
    borderBottomColor: 'transparent'
  },
  '@media(prefers-reduced-motion:reduce)': {
    transition: 'none'
  },

  Icon: {
    fontSize: 'B',
    name: (el, s) => s.display?.icon || s.kind || 'grid',
    color: (el, s) => s.display?.color || 'title',
    flexShrink: '0'
  },

  Info: {
    flow: 'y',
    gap: 'X2',
    flex: '1 1 0',
    minWidth: '0',

    RowLabel: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '600',
      color: 'title',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.display?.label || s.kind || ''
    },

    RowTagline: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      fontWeight: '400',
      color: 'paragraph',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.display?.tagline || s.display?.description || ''
    }
  },

  InstallableStateBadge: {
    flexShrink: '0'
  },

  ChevronIcon: {
    extends: 'Icon',
    name: 'arrow right',
    fontSize: 'Z2',
    color: 'caption',
    flexShrink: '0'
  },

  onClick: (e, el, s) => {
    el.call('openInstallableModal', {
      domain: s.domain,
      kind: s.kind,
      installed: s.installed
    })
  }
}

// ── Unified item — selects variant based on _layout in state ──────────
// Parent InstallableRail passes `_layout` in each child state entry.

export const InstallableRailItem = {
  InstallableRailChip: {
    hide: (el, s) => s._layout !== 'rail',
    domain: (el, s) => s.domain,
    kind: (el, s) => s.kind,
    display: (el, s) => s.display,
    installed: (el, s) => s.installed
  },

  InstallableGridCard: {
    hide: (el, s) => s._layout !== 'grid',
    domain: (el, s) => s.domain,
    kind: (el, s) => s.kind,
    display: (el, s) => s.display,
    installed: (el, s) => s.installed
  },

  InstallableListRow: {
    hide: (el, s) => s._layout !== 'list',
    domain: (el, s) => s.domain,
    kind: (el, s) => s.kind,
    display: (el, s) => s.display,
    installed: (el, s) => s.installed
  }
}
